import type { Ref } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useContentToken } from '@vibesites/runtime/react';
import { EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

interface PageBlurbProps {
  /** Property field name for the blurb (e.g. 'property_floorplans_blurb') */
  blurbField?: string;
  /** Unique prefix so each page's blurb token is independently editable */
  tokenPrefix?: string;
  /** Static fallback text when no property blurb is available */
  defaultText?: string;
}

export function PageBlurb({ blurbField, tokenPrefix, defaultText }: PageBlurbProps) {
  useRevealStyles();
  const sectionRef = useScrollReveal<HTMLElement>();

  const prefix = tokenPrefix || 'pageblurb';
  const titlePath = `${prefix}.title`;
  const textPath = `${prefix}.text`;
  const titleValue = useContentToken(titlePath, { defaultValue: '' });

  return (
    <section ref={sectionRef as Ref<HTMLElement>} className="reveal-fade-up pt-10 pb-4 md:pt-14 md:pb-6 bg-[var(--vibe-color-background)]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {titleValue ? (
          <EditableToken
            path={titlePath}
            as="h2"
            className="mb-4 font-heading text-2xl font-normal text-[var(--vibe-color-text)] sm:text-3xl"
          >
            {titleValue}
          </EditableToken>
        ) : null}
        <RichTextContent
          path={textPath}
          defaultField={blurbField}
          defaultValue={defaultText || ''}
          as="div"
          className="font-body text-sm md:text-lg leading-relaxed text-[var(--vibe-color-text-muted)] space-y-4 [&_a]:text-primary [&_a]:underline [&_p]:mb-0"
        />
      </div>

      {/* Schema-lint anchors for static analysis — hidden at runtime */}
      <EditableToken path="pageblurb.title" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <RichTextContent
        path="pageblurb.text"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageBlurb',
  displayName: 'Page Blurb',
  tokens: {
    'pageblurb.title': {
      label: 'Body Title',
      type: 'text',
      defaultValue: '',
    },
    'pageblurb.text': {
      label: 'Blurb Text',
      type: 'richtext',
      defaultValue: '',
    },
  },
};

(PageBlurb as any).contentSchema = contentSchema;

export default PageBlurb;
